/**
 * Retry Queue Service for handling failed uploads
 */

export interface RetryQueueItem {
  id: string;
  blob: Blob;
  partNumber: number;
  createdBy: string;
  scribeJobId: string;
  retryCount: number;
  maxRetries: number;
  lastRetryTime: number;
  isFinalPart: boolean;
  durationSeconds: number; // Duration of the audio chunk in seconds
  dxList?: any; // Optional message data from parent window
  uploadFunction: (item: RetryQueueItem) => Promise<void>;
}

export interface RetryQueueConfig {
  maxRetries: number;
  initialDelay: number;
  backoffMultiplier: number;
  maxDelay: number;
  retryInterval: number;
}

export class RetryQueueService {
  private queue: RetryQueueItem[] = [];
  private isProcessing: boolean = false;
  private processingInterval: NodeJS.Timeout | null = null;
  private config: RetryQueueConfig;

  constructor(config: Partial<RetryQueueConfig> = {}) {
    this.config = {
      maxRetries: 3,
      initialDelay: 1000,
      backoffMultiplier: 2,
      maxDelay: 30000,
      retryInterval: 5000,
      ...config,
    };
  }

  /**
   * Add item to retry queue
   */
  addToQueue(
    item: Omit<RetryQueueItem, 'id' | 'retryCount' | 'lastRetryTime'>
  ): void {
    const queueItem: RetryQueueItem = {
      ...item,
      id: this.generateId(),
      retryCount: 0,
      lastRetryTime: 0,
    };

    this.queue.push(queueItem);
    // Start processing if not already running
    if (!this.isProcessing) {
      this.startProcessing();
    }
  }

  /**
   * Start processing the retry queue
   */
  startProcessing(): void {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.processQueue();
  }

  /**
   * Stop processing the retry queue
   */
  stopProcessing(): void {
    this.isProcessing = false;
    if (this.processingInterval) {
      clearTimeout(this.processingInterval);
      this.processingInterval = null;
    }
  }

  /**
   * Process items in the retry queue
   */
  private async processQueue(): Promise<void> {
    if (!this.isProcessing || this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    const now = Date.now();
    const readyItems = this.queue.filter((item) => {
      const delay = this.calculateDelay(item.retryCount);
      return now - item.lastRetryTime >= delay;
    });

    for (const item of readyItems) {
      try {
        await item.uploadFunction(item);

        // Success - remove from queue
        this.removeFromQueue(item.id);
      } catch (error) {
        console.error(`❌ Upload failed: ${item.id}`, error);

        item.retryCount++;
        item.lastRetryTime = now;

        if (item.retryCount > item.maxRetries) {
          // Max retries reached - remove from queue
          this.removeFromQueue(item.id);
          console.error(`💀 Max retries reached for: ${item.id}`);
        }
      }
    }

    // Schedule next processing
    this.processingInterval = setTimeout(() => {
      this.processQueue();
    }, this.config.retryInterval);
  }

  /**
   * Remove item from queue
   */
  private removeFromQueue(id: string): void {
    const index = this.queue.findIndex((item) => item.id === id);
    if (index > -1) {
      this.queue.splice(index, 1);
    }
  }

  /**
   * Calculate delay for retry based on retry count
   */
  private calculateDelay(retryCount: number): number {
    const delay =
      this.config.initialDelay *
      Math.pow(this.config.backoffMultiplier, retryCount);
    return Math.min(delay, this.config.maxDelay);
  }

  /**
   * Generate unique ID for queue item
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get queue status
   */
  getStatus(): { queueLength: number; isProcessing: boolean } {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing,
    };
  }

  /**
   * Clear all items from queue
   */
  clearQueue(): void {
    this.queue = [];
  }
}

// Export singleton instance
export const retryQueueService = new RetryQueueService();
